import React from 'react';
import styled from 'styled-components';
import { Paper, Typography, Grid, LinearProgress } from '@mui/material';
import HeroImage from './HeroImage';
import { Team } from '../models/DraftState';

const TeamCard = styled(Paper)`
  padding: 16px;
  background-color: ${props => props.teamcolor === 'radiant' ? 'rgba(0, 100, 0, 0.2)' : 'rgba(139, 0, 0, 0.2)'};
  border: 1px solid ${props => props.teamcolor === 'radiant' ? 'rgba(0, 255, 0, 0.3)' : 'rgba(255, 0, 0, 0.3)'};
  margin-bottom: 16px;
`;

const TeamHeader = styled(Typography)`
  color: ${props => props.teamcolor === 'radiant' ? '#4CAF50' : '#F44336'};
  font-weight: bold;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeroesContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin-bottom: 16px;
`;

const HeroSlot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeroName = styled(Typography)`
  font-size: 12px;
  color: #ccc;
  margin-top: 8px;
  text-align: center;
  width: 70px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatsContainer = styled.div`
  margin-top: 16px;
`;

const StatRow = styled.div`
  margin-bottom: 8px;
`;

const StatLabel = styled(Typography)`
  color: #ccc;
  margin-bottom: 4px;
  display: flex;
  justify-content: space-between;
`;

const StatValue = styled.span`
  color: #fff;
  font-weight: bold;
`;

const StyledLinearProgress = styled(LinearProgress)`
  height: 8px;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.1);
  
  .MuiLinearProgress-bar {
    background-color: ${props => {
      switch (props.stattype) {
        case 'damage': return '#F44336';
        case 'durability': return '#2196F3';
        case 'control': return '#9C27B0';
        case 'mobility': return '#FF9800';
        case 'push': return '#4CAF50';
        default: return '#FFFFFF';
      }
    }};
  }
`;

/**
 * TeamComposition Component
 * 
 * @param {Object} props - Component props
 * @param {string} props.team - Team identifier (radiant or dire)
 * @param {Array} props.heroes - List of heroes in the team
 * @param {Object} props.stats - Team composition stats
 * @param {Function} props.onHeroClick - Callback when a hero is clicked
 */
const TeamComposition = ({ 
  team, 
  heroes = [], 
  stats = { damage: 0, durability: 0, control: 0, mobility: 0, push: 0 },
  onHeroClick = null
}) => {
  const emptySlots = 5 - heroes.length;
  const teamName = team === Team.RADIANT ? 'Radiant' : 'Dire';
  const teamColor = team === Team.RADIANT ? 'radiant' : 'dire';

  return (
    <TeamCard teamcolor={teamColor}>
      <TeamHeader variant="h6" teamcolor={teamColor}>
        {teamName} Team
        <span>{heroes.length}/5</span>
      </TeamHeader>
      
      <HeroesContainer>
        {heroes.map((hero, index) => (
          <HeroSlot key={index}>
            <HeroImage 
              heroName={hero.name} 
              size="50px" 
              onClick={() => onHeroClick && onHeroClick(index)} 
            />
            <HeroName variant="caption">{hero.name}</HeroName>
          </HeroSlot>
        ))}
        
        {/* Empty hero slots */}
        {Array(emptySlots).fill(0).map((_, index) => (
          <HeroSlot key={`empty-${index}`}>
            <HeroImage 
              heroName="" 
              size="50px" 
              onClick={() => onHeroClick && onHeroClick(heroes.length + index)} 
            />
            <HeroName variant="caption">Empty</HeroName>
          </HeroSlot>
        ))}
      </HeroesContainer>
      
      {heroes.length > 0 && (
        <StatsContainer>
          <StatRow>
            <StatLabel variant="body2">
              Damage <StatValue>{stats.damage.toFixed(1)}</StatValue>
            </StatLabel>
            <StyledLinearProgress 
              variant="determinate" 
              value={Math.min(stats.damage * 10, 100)} 
              stattype="damage" 
            />
          </StatRow>
          
          <StatRow>
            <StatLabel variant="body2">
              Durability <StatValue>{stats.durability.toFixed(1)}</StatValue>
            </StatLabel>
            <StyledLinearProgress 
              variant="determinate" 
              value={Math.min(stats.durability * 10, 100)} 
              stattype="durability" 
            />
          </StatRow>
          
          <StatRow>
            <StatLabel variant="body2">
              Control <StatValue>{stats.control.toFixed(1)}</StatValue>
            </StatLabel>
            <StyledLinearProgress 
              variant="determinate" 
              value={Math.min(stats.control * 10, 100)} 
              stattype="control" 
            />
          </StatRow>
          
          <StatRow>
            <StatLabel variant="body2">
              Mobility <StatValue>{stats.mobility.toFixed(1)}</StatValue>
            </StatLabel>
            <StyledLinearProgress 
              variant="determinate" 
              value={Math.min(stats.mobility * 10, 100)} 
              stattype="mobility" 
            />
          </StatRow>
          
          <StatRow>
            <StatLabel variant="body2">
              Push <StatValue>{stats.push.toFixed(1)}</StatValue>
            </StatLabel>
            <StyledLinearProgress 
              variant="determinate" 
              value={Math.min(stats.push * 10, 100)} 
              stattype="push" 
            />
          </StatRow>
        </StatsContainer>
      )}
    </TeamCard>
  );
};

export default TeamComposition; 